import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, List, ListItem, ListItemAvatar,
  Avatar, ListItemText, Typography, Box, Alert, CircularProgress
} from '@mui/material';
import { ArrowBack, Search, Send } from '@mui/icons-material';
import axios from 'axios';

const API_CHAT = "https://apiweb.hitpoly.com/ajax/chatCRMController.php";

const SharePostModal = ({ open, onClose, post, currentUserId, currentUserName, queryUsers, activeProfile }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const [sentTo, setSentTo] = useState(() => new Set());
  const [snackbar, setSnackbar] = useState(null);
  const friendsLoaded = useRef(false);
  const alertRef = useRef(null);

  useEffect(() => {
    if (snackbar) {
      const t = setTimeout(() => setSnackbar(null), 3000);
      return () => clearTimeout(t);
    }
  }, [snackbar]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setResults([]);
      setSentTo(new Set());
      friendsLoaded.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && !friendsLoaded.current) {
      setLoadingFriends(true);
      queryUsers('', (data) => {
        setFriends(data);
        setResults(data);
        setLoadingFriends(false);
        friendsLoaded.current = true;
      }, true);
    }
  }, [open, queryUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim()) {
        queryUsers(search, (data) => {
          const filtered = data.filter(u => String(u.id) !== String(currentUserId));
          setResults(filtered);
        }, false);
      } else {
        setResults(friends);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, queryUsers, currentUserId, friends]);

  const handleSend = async (user) => {
    setSendingTo(user.id);
    const profileId = activeProfile?.id || currentUserId;
    const profileType = activeProfile?.type || 'main';
    try {
      const params = {
        accion: 'iniciar_sesion',
        user_1_id: profileId,
        user_2_id: user.id,
        profile_id: profileId,
        profile_type: profileType,
        context_id: profileId,
        context_type: profileType,
        project_source: 'comunidad'
      };
      const convRes = await axios.post(API_CHAT, params);
      if (convRes.data.success) {
        const convId = convRes.data.conversation_id;
        const postContent = post.content || '';
        const postImage = post.image_url || '';
        const postExcerpt = postContent.length > 200 ? postContent.substring(0, 200) + '...' : postContent;
        const postPayload = {
          type: 'shared_post',
          postId: post.id,
          mediaUrl: postImage,
          title: postExcerpt || 'Publicación de la comunidad',
          authorName: post.user_full_name || post.nombre_real || 'Hitpoly',
          authorAvatar: post.user_avatar || '',
          targetUrl: `${window.location.origin}/systems/comunidad?postId=${post.id}`
        };
        const msgText = JSON.stringify(postPayload);
        const msgParams = {
          accion: 'sendMessage',
          conversation_id: convId,
          sender_id: profileId,
          sender_profile_id: profileId,
          sender_profile_type: profileType,
          text: msgText
        };
        const msgRes = await axios.post(API_CHAT, msgParams);
        setSentTo(prev => new Set(prev).add(user.id));
        setSnackbar({ severity: 'success', message: `Publicación enviada a ${user.nombre || user.display || 'usuario'}` });
        window.dispatchEvent(new CustomEvent('chat-activity', { detail: { conversationId: convId } }));
      } else {
        setSnackbar({ severity: 'error', message: `Error: ${convRes.data.message || 'Error al crear conversación'}` });
      }
    } catch (e) {
      setSnackbar({ severity: 'error', message: 'Error al enviar' });
    } finally {
      setSendingTo(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: 99999 }}
        PaperProps={{ sx: { borderRadius: 3, height: { xs: '80vh', md: 'auto' }, maxHeight: { xs: '600px', md: 'calc(100vh - 120px)' }, display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e4e6eb', display: 'flex', alignItems: 'center', p: 1.5 }}>
          <Button onClick={onClose} sx={{ minWidth: 40, p: 0, color: '#65676b' }}><ArrowBack /></Button>
          <Typography sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' }}>Compartir publicación</Typography>
          <Box sx={{ width: 40 }} />
        </DialogTitle>

        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
          <TextField
            autoFocus fullWidth size="small" value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Busca personas..."
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              sx: { borderRadius: 5, bgcolor: '#f0f2f5', '& fieldset': { border: 'none' } }
            }}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, px: 1 }}>
            {search ? 'Resultados' : 'Tus conexiones'}
          </Typography>

          <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {loadingFriends && search === '' && results.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loadingFriends && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 5 }}>
                {search ? 'No se encontraron personas' : 'No tienes conexiones'}
              </Typography>
            )}
            {results.map(user => (
              <ListItem key={user.id} sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: '#f2f2f2' } }}>
                <ListItemAvatar>
                  <Avatar src={user.avatar}>{user.nombre?.charAt(0)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.display || `${user.nombre || ''} ${user.apellido || ''}`.trim()}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={sendingTo === user.id || sentTo.has(user.id)}
                  onClick={() => handleSend(user)}
                  sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#1877f2', minWidth: 80 }}
                  endIcon={sendingTo === user.id ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Send />}
                >
                  {sentTo.has(user.id) ? 'Enviado' : sendingTo === user.id ? '' : 'Enviar'}
                </Button>
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e4e6eb' }}>
          <Button onClick={onClose} sx={{ color: '#1877f2', fontWeight: 600, textTransform: 'none' }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {snackbar && ReactDOM.createPortal(
        <Box ref={alertRef} sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2147483647,
        }}>
          <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            {snackbar.message}
          </Alert>
        </Box>,
        document.body
      )}
    </>
  );
};

export default SharePostModal;